import { useEffect, useRef, useState } from 'react'
import { BadgeCheck } from 'lucide-react'
import { Button } from '@/components/ui'
import { Crest } from '@/components/Logo'
import { formatDate, formatSek, formatTime } from '@/lib/format'

export interface RedeemResult {
  shortId: string
  redeemedAt: number
  valueSek: number
  offerTitle: string
  companyName: string
  offerText: string
  totalSavedSek: number
  /** Instruktion till kassören, t.ex. ett PLU-nummer. Ofta det enda hen läser. */
  staffInstructions?: string | null
}

/**
 * Kvittot som visas för butikspersonalen direkt efter inlösen.
 *
 * På ett färskt kvitto tickar klockan sekund för sekund. Det är medvetet: en
 * skärmdump av den här vyn står stilla, en riktig inlösen gör det inte.
 * Tillsammans med kvittonumret räcker det för att personalen ska kunna lita
 * på skärmen utan att företaget behöver logga in någonstans.
 *
 * Visas kvittot i efterhand står klockslaget för inlösen still i stället, och
 * en rad förklarar att det är ett sparat kvitto. Att låta klockan ticka då
 * vore att låtsas att en gammal inlösen precis hänt.
 */
export function RedeemConfirmation({
  result,
  historic = false,
  onClose,
}: {
  result: RedeemResult
  /** True när kvittot visas i efterhand. Då tickar ingen klocka. */
  historic?: boolean
  onClose: () => void
}) {
  const [now, setNow] = useState(() => new Date())

  /**
   * Klockan tickar bara på ett färskt kvitto.
   *
   * På ett återvisat kvitto vore den direkt vilseledande – den skulle få en
   * inlösen från förra veckan att se ut som att den skedde nyss, vilket är
   * precis den sortens sak butikspersonalen förlitar sig på att den inte gör.
   */
  useEffect(() => {
    if (historic) return
    // Rikta in första ticket mot nästa hela sekund, annars driver visningen
    // och hoppar över en sekund då och då.
    let interval = 0
    const align = window.setTimeout(() => {
      setNow(new Date())
      interval = window.setInterval(() => setNow(new Date()), 1000)
    }, 1000 - (Date.now() % 1000))

    return () => {
      window.clearTimeout(align)
      window.clearInterval(interval)
    }
  }, [historic])

  /** Fanfaren hör hemma vid stämpelns nedslag, inte vid nätverksanropet. */
  useEffect(() => {
    if (historic) return
    const timer = window.setTimeout(() => navigator.vibrate?.([12, 40, 24]), 260)
    return () => window.clearTimeout(timer)
  }, [historic])

  const redeemedAt = new Date(result.redeemedAt)
  const clock = now.toLocaleTimeString('sv-SE', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })

  /*
   * Fokus in i rutan när den öppnas.
   *
   * Utan det står fokus kvar på erbjudanderaden bakom, och den som använder
   * tangentbord tabbar vidare i ett häfte med trettio rader utan att förstå
   * att något hänt.
   */
  const ruta = useRef<HTMLDivElement>(null)
  useEffect(() => {
    ruta.current?.focus()
  }, [])

  return (
    <div
      /*
       * Kvittot är appens enda oåterkalleliga ögonblick, och det annonserades
       * inte alls. En skärmläsaranvändare som just lyckats lösa in fick
       * ingenting: varken att det gick, vilket kvittonummer som gäller eller
       * vad kassören ska titta på. Rutan låg som en vanlig div ovanpå sidan.
       *
       * role och aria-modal säger vad den är. Fokus flyttas hit vid
       * montering, så att tangentbordet hamnar i rutan och inte kvar bakom
       * den. aria-live-raden längre ned läser upp resultatet.
       */
      role="dialog"
      aria-modal="true"
      aria-label="Kvitto på använd rabatt"
      ref={ruta}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-brand-surface px-5 pt-safe pb-safe text-on-brand outline-none"
    >
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center gap-6 py-10 text-center">
        {/*
         * Det skärmläsaren faktiskt får höra, i en mening.
         *
         * Stämpeln ovanför är grafik, och siffrorna nedanför står i en
         * definitionslista som läses upp först när man tar sig dit. Den som
         * står vid kassan behöver beskedet direkt.
         */}
        <p className="sr-only" role="status" aria-live="assertive">
          {historic
            ? `Kvitto: ${result.offerText} hos ${result.companyName} är använd. Kvittonummer ${result.shortId}.`
            : `Rabatten är använd. ${result.offerText} hos ${result.companyName}. Kvittonummer ${result.shortId}.`}
        </p>

        <div className="relative flex items-center justify-center">
          <div
            aria-hidden
            className="absolute size-44 rounded-full border-4 border-accent/25 motion-safe:animate-[fade-in_400ms_ease-out]"
          />
          <div className="flex size-36 rotate-[-8deg] items-center justify-center rounded-3xl border-[6px] border-accent motion-safe:animate-[stamp-in_520ms_var(--ease-spring)]">
            <span className="font-display text-2xl leading-none font-extrabold tracking-tight text-accent">
              ANVÄND
            </span>
          </div>
        </div>

        <div className="space-y-1">
          <p className="font-display text-sm font-bold tracking-[0.18em] text-accent uppercase">
            {result.companyName}
          </p>
          <h1 className="font-display text-2xl font-extrabold text-balance">{result.offerTitle}</h1>
          <p className="text-on-brand/85">{result.offerText}</p>
        </div>

        {/* Det kassören behöver göra, före kvittouppgifterna. På pappret står
            det tryckt på kupongen; här är det den viktigaste raden på skärmen
            och därför i gult, som är det enda som får sticka ut mer än
            stämpeln. */}
        {result.staffInstructions && (
          <div className="w-full rounded-card bg-accent px-4 py-3 text-left text-on-accent">
            <p className="font-display text-xs font-bold tracking-[0.16em] uppercase opacity-70">
              Till kassören
            </p>
            <p className="mt-0.5 font-display text-lg font-extrabold text-balance">
              {result.staffInstructions}
            </p>
          </div>
        )}

        {/* Kvittouppgifterna som personalen tittar på. */}
        <dl className="w-full space-y-2 rounded-card border border-white/15 bg-white/10 p-4 text-left text-sm">
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-on-brand/70">Datum</dt>
            <dd className="font-semibold">{formatDate(redeemedAt)}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-on-brand/70">{historic ? 'Inlöstes' : 'Klockan nu'}</dt>
            <dd className="font-mono text-lg font-bold tabular-nums text-accent">
              {historic ? formatTime(redeemedAt) : clock}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-on-brand/70">Kvittonummer</dt>
            <dd className="font-mono font-bold tracking-widest">{result.shortId}</dd>
          </div>
        </dl>

        {historic && (
          <p className="text-sm text-on-brand/70 text-balance">
            Det här är ett sparat kvitto. Rabatten löstes in {formatDate(redeemedAt)} och är redan
            förbrukad.
          </p>
        )}

        <div className="flex items-center gap-2 rounded-pill bg-accent px-4 py-2 font-display font-extrabold text-on-accent">
          <BadgeCheck size={20} aria-hidden />
          Du har sparat {formatSek(result.totalSavedSek)} i år
        </div>

        <Crest size={44} className="opacity-80" />

        <Button variant="accent" size="lg" fullWidth onClick={onClose}>
          Klart
        </Button>
      </div>
    </div>
  )
}
